import { z } from 'zod';
// Lifecycle state of a materialised sandbox instance (see
// docs/strategy-sandboxes.md). A row is created in `provisioning`, flips to
// `ready` once the provider instance is usable, goes `paused` while the
// provider holds it paused (idle: its disk in storage, no place on a host) and
// `ready` again once resumed, and ends in `dead` (provider instance gone),
// `error` (creation failed) or `destroyed` (explicit teardown). "Active" =
// provisioning | ready. Single source of truth, reused by the storage layer and
// the wire schema; the DB enforces the same set via a CHECK constraint
// (migrations 022, 043).
export const SandboxStateSchema = z.enum(['provisioning', 'ready', 'paused', 'error', 'dead', 'destroyed']);
// Wire shape of a sandbox record (ISO-8601 timestamps). `id` is our own,
// decoupled from `providerSandboxId` (the execution service handle) so the
// backend stays interchangeable. `provider`/`pool`/`size`/`image` are verbatim
// (no fixed enum). Attribution carries `userId` (always) and `organizationId`
// (nullable, posed for the future). Timestamps after creation are optional
// (set as the instance reaches each state).
export const SandboxRecordSchema = z.object({
    id: z.string(),
    conversationId: z.string(),
    userId: z.string(),
    organizationId: z.string().optional(),
    provider: z.string(),
    providerSandboxId: z.string().optional(),
    pool: z.string().optional(),
    size: z.string().optional(),
    image: z.string().optional(),
    state: SandboxStateSchema,
    restoredFrom: z.string().optional(),
    error: z.string().optional(),
    createdAt: z.string(),
    readyAt: z.string().optional(),
    lastHeartbeatAt: z.string().optional(),
    terminatedAt: z.string().optional()
});
// A conversation's sandbox as its panel shows it: none (yet, or none left: the
// next message starts an empty one), starting, active, or paused (the next
// message resumes it), with when it entered that status (ISO-8601).
export const ConversationSandboxStatusSchema = z.enum(['none', 'starting', 'active', 'paused']);
export const ConversationSandboxSchema = z.object({
    status: ConversationSandboxStatusSchema,
    since: z.string().optional()
});
